import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { ArrowRight, Check, Wrench, Hammer, Paintbrush, Zap, Armchair, Snowflake } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import Marquee from "@/components/Marquee";
import Activities from "@/components/Activities";
import { useSettings, mediaUrl } from "@/context/SettingsContext";

const ICONS = [Wrench, Hammer, Paintbrush, Zap, Armchair, Snowflake];

const Chapter = ({ service, index, quoteCta, specsLabel, image, settings }) => {
  const even = index % 2 === 0;
  const Icon = ICONS[index];
  return (
    <Reveal>
      <article
        className={`grid items-center gap-10 border-t border-brand/10 py-16 sm:py-20 lg:grid-cols-2 lg:gap-16 ${
          even ? "" : "lg:[direction:rtl]"
        }`}
        data-testid={`manifesto-chapter-${service.num}`}
      >
        <div className="lg:[direction:ltr]">
          <span className="font-mono text-5xl font-light text-gold sm:text-7xl">
            {service.num}
          </span>
          <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">
            {service.title}
          </h2>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.25em] text-brand">
            {service.tagline}
          </p>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-hover">
            {service.desc}
          </p>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
            {specsLabel}
          </p>
          <ul className="mt-4 space-y-3">
            {service.specs.map((spec) => (
              <li key={spec} className="flex items-start gap-3 text-sm text-forest">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-mint">
                  <Check className="h-3 w-3 text-brand" />
                </span>
                {spec}
              </li>
            ))}
          </ul>
          <Link
            to={`/contact?type=devis&service=${encodeURIComponent(service.title)}#formulaire`}
            data-testid={`chapter-${service.num}-quote-button`}
            className="group mt-8 inline-flex items-center gap-3 rounded-full border-2 border-gold px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-white"
          >
            {quoteCta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </div>

        <div className="lg:[direction:ltr]">
          <div
            className={`group relative aspect-[4/3] overflow-hidden rounded-[2.5rem] p-10 ${
              even ? "bg-mint" : "bg-cream"
            }`}
          >
            {image && <img src={mediaUrl(image)} alt={service.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid={`service-image-${index}`} />}
            {image && <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/20 to-transparent" />}
            <div className="dots-bg absolute inset-0 opacity-50" />
            <span
              className={`font-display absolute -bottom-6 -right-2 text-[11rem] font-extrabold leading-none sm:text-[14rem] ${
                even ? "text-brand/10" : "text-gold/10"
              }`}
            >
              {service.num}
            </span>
            <div className="relative flex h-full flex-col justify-between">
              <span
                className={`flex h-20 w-20 items-center justify-center rounded-3xl text-white shadow-xl transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110 ${
                  even ? "bg-brand shadow-brand/30" : "bg-gold shadow-gold/30"
                }`}
              >
                <Icon className="h-9 w-9" />
              </span>
              <div>
                <p className={`font-display text-2xl font-extrabold sm:text-3xl ${image ? "text-white" : even ? "text-forest" : "text-gold"}`}>
                  {service.title}
                </p>
                <p className={`mt-1 font-mono text-[11px] uppercase tracking-[0.25em] ${image ? "text-white/85" : "text-brand"}`}>
                  {settings.company_name} · {service.tagline}
                </p>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

const Services = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const items = t.services.items.map((x, i) => ({ ...x, ...(settings.content?.services?.[i] || {}) }));
  return (
    <main data-testid="services-page">
      <Seo page="services" />
      <section className="relative overflow-hidden pb-16 pt-40 sm:pb-20" data-testid="services-header">
        <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-mint blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead as="h1" overline={t.services.overline} title={t.services.title} accent={t.services.titleAccent} sub={t.services.sub} />
        </div>
      </section>
      <Marquee />
      <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        {items.map((s, i) => (
          <Chapter
            key={s.num}
            service={s}
            index={i}
            quoteCta={t.services.quoteCta}
            specsLabel={t.services.specsLabel}
            image={settings.content?.service_images?.[i]}
            settings={settings}
          />
        ))}
      </div>
      <Activities />
    </main>
  );
};

export default Services;
